import {
  OriginSystem,
  PaymentAcceptanceRules,
  PaymentRulesResponseV1,
  SessionDocumentInfo,
  TransactionPayableV1,
} from '../models';

export const mapTransactionPayables = (
  documents: SessionDocumentInfo[],
): TransactionPayableV1[] => {
  return documents
    .filter((d) => d.isPayable)
    .map((d) => {
      const transactionPayable: TransactionPayableV1 = {
        transactionAmount: d.payableAmount,
        urn: d.urn,
      };

      transactionPayable.appliedDiscount = d.appliedDiscountAmount || 0;

      if (d.isCreditMemo) {
        return transactionPayable;
      }

      if (d.payableAmount < d.originalPayableAmount) {
        transactionPayable.isShortPay = true;
        transactionPayable.adjustments = {
          shortPay: {
            reasonText: d.description,
            disputeReasonId: d.disputeReasonId,
          },
        };
      } else if (d.payableAmount > d.originalPayableAmount) {
        transactionPayable.isOverPay = true;
        transactionPayable.adjustments = {
          overPay: {
            reasonText: d.description,
          },
        };
      }

      if (d.surchargeAmount != null) {
        transactionPayable.surcharge = d.surchargeAmount ?? 0.0;
      }

      return transactionPayable;
    });
};

export const mapPaymentRules = (
  paymentAcceptanceRules: PaymentAcceptanceRules,
  isStripePermitted: boolean,
  originApplication: string,
): PaymentRulesResponseV1 => {
  const {
    domain,
    enhancedAutopaySettings,
    cardSettings,
    bankSettings,
    achSettings,
    adjustmentsSettings,
    surchargingSettings,
    generalPaymentSettings,
    paymentLinksSettings,
    gracePeriodSettings,
    flexibleTemplateSettings,
    hostedPaymentsValidatingSettings,
    hostedPaymentsNonValidatingSettings,
    cvvCardOnFileSettings,
    statementPaymentsSettings,
    timedCardAcceptanceSettings,
    contactInfoSettings: contactSettings,
    stripeSettings,
    customTemplatesSettings,
  } = paymentAcceptanceRules;
  originApplication = originApplication.toLowerCase();
  const shortAndOverPaySettings = adjustmentsSettings?.shortAndOverPay;

  const achApplicationSettings = achSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === originApplication,
  );

  const autopayAchApplicationSettings = achSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === 'autopay',
  );

  const bankApplicationSettings = bankSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === originApplication,
  );

  const autopayBankApplicationSettings = bankSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === 'autopay',
  );

  const ccApplicationSettings = cardSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === originApplication,
  );

  const shortAndOverPayApplicationSettings =
    shortAndOverPaySettings?.applications?.find(
      (app) => app.name.toLowerCase() === originApplication,
    );

  const flexibleTemplateApplicationSettings =
    flexibleTemplateSettings?.applications?.find(
      (app) => app.originApplication.toLowerCase() === originApplication,
    );

  const hostedPaymentsPageSettings =
    originApplication === 'hostedpaymentsvalidating'
      ? hostedPaymentsValidatingSettings
      : hostedPaymentsNonValidatingSettings;

  const cvvCardApplicationSettings = cvvCardOnFileSettings?.applications?.find(
    (app) => app.originApplication.toLowerCase() === originApplication,
  );

  const allowStatementPaymentsSettings =
    statementPaymentsSettings?.applications?.find(
      (app) => app.originApplication.toLowerCase() === originApplication,
    );

  const timedCardAcceptanceApplicationSettings =
    timedCardAcceptanceSettings?.applications?.find(
      (app) => app.originApplication.toLowerCase() === originApplication,
    );

  const isStripeAllowedForOriginApp =
    stripeSettings?.applications?.find(
      (app) => app.originApplication.toLowerCase() === originApplication,
    )?.allowStripePayments ?? false;

  const paymentRules: PaymentRulesResponseV1 = {
    tenantId: domain?.tenantId,
    originApplication: domain?.originApplication,
    isStripePermitted: isStripePermitted && isStripeAllowedForOriginApp,
    settings: {
      payments: {
        requireCVV: ccApplicationSettings?.requireCVV,
        acceptBank:
          bankApplicationSettings?.allowBankPayments ??
          achApplicationSettings?.allowACHPayments,
        autoPayAcceptBank:
          autopayBankApplicationSettings?.allowBankPayments ??
          autopayAchApplicationSettings?.allowACHPayments,
        acceptCreditCard: ccApplicationSettings?.allowCardPayments,
        allowableCardBrands: cardSettings?.acceptedCardBrands,
        acceptDirectDebit:
          bankApplicationSettings?.allowBankPayments ??
          achApplicationSettings?.allowACHPayments,
      },
      cardOnFile: {
        useSavedPaymentMethods:
          generalPaymentSettings?.useSavedPaymentMethod ?? true,
        createSavedPaymentMethod:
          generalPaymentSettings?.createSavedPaymentMethod ?? true,
      },
      surcharge: {
        allowSurcharging: surchargingSettings?.allowSurcharging,
        restrictByCardType: surchargingSettings?.restrictByCardType,
        restrictByZipCode: surchargingSettings?.restrictByPostalCode,
      },
      generalPaymentSettings: {
        zeroDollarPaymentsEnabled:
          generalPaymentSettings?.zeroDollarPaymentsEnabled,
        blockDuplicatePayments: generalPaymentSettings?.blockDuplicatePayments,
      },
      shortAndOverPaySettings: {
        shortPayNotificationEmails: shortAndOverPaySettings?.emails,
        allowDisputeReason: shortAndOverPaySettings?.allowDisputeReason,
        allowNonDisputeReason: shortAndOverPaySettings?.allowNonDisputeReason,
        allowFreeFormReason: shortAndOverPaySettings?.allowFreeFormReason,
        allowShortPay: shortAndOverPayApplicationSettings?.shortPay?.enabled,
        allowOverPay: shortAndOverPayApplicationSettings?.overPay?.enabled,
      },
      paymentLinksSettings: {
        daysUntilLinkExpiration: paymentLinksSettings?.daysUntilLinkExpiration,
        includeContactInfo: paymentLinksSettings?.includeContactInfo,
        contactInfoMessage: paymentLinksSettings?.contactInfoMessage,
        allowBuyerLogin: paymentLinksSettings?.allowBuyerLogin,
      },
      gracePeriodSettings: {
        applications: gracePeriodSettings?.applications,
        gracePeriodType: gracePeriodSettings?.gracePeriodType,
        gracePeriodNumberOfDays: gracePeriodSettings?.gracePeriodNumberOfDays,
      },
      flexibleTemplateSettings: {
        restrictionType: flexibleTemplateSettings?.restrictionType,
        allowFlexibleTemplate:
          flexibleTemplateApplicationSettings?.allowFlexibleTemplate,
        templateRules: flexibleTemplateSettings?.templateRules,
        deactivatedTemplateIds:
          flexibleTemplateSettings?.deactivatedTemplateIds,
      },
      hostedPaymentPageSettings: {
        buyerCustomIdentifierFieldLabel:
          hostedPaymentsPageSettings?.buyerCustomIdentifierFieldLabel,
        captchaEnabled: hostedPaymentsPageSettings?.captchaEnabled,
        hostedPageUrl: hostedPaymentsPageSettings?.hostedPageUrl,
        hostedPaymentNotes: hostedPaymentsPageSettings?.hostedPaymentNotes,
      },
      cvvCardOnFileSettings: {
        applications: cvvCardApplicationSettings
          ? [
              {
                originApplication:
                  cvvCardApplicationSettings?.originApplication,
                originApplicationDisplayName:
                  cvvCardApplicationSettings?.originApplicationDisplayName,
                requireCvvCardOnFile:
                  cvvCardApplicationSettings?.requireCvvCardOnFile,
              },
            ]
          : [],
      },
      statementPaymentsSettings: {
        applications: allowStatementPaymentsSettings
          ? [
              {
                originApplication:
                  allowStatementPaymentsSettings?.originApplication,
                originApplicationDisplayName:
                  allowStatementPaymentsSettings?.originApplicationDisplayName,
                allowStatementPayments:
                  allowStatementPaymentsSettings?.allowStatementPayments,
              },
            ]
          : [],
      },
      enhancedAutopaySettings: {
        autoPayVersion: enhancedAutopaySettings?.autoPayVersion,
      },
      contactInfoSettings: {
        includeContactInfo: contactSettings?.includeContactInfo,
        contactInfoMessage: contactSettings?.contactInfoMessage,
      },
      timedCardAcceptanceSettings: {
        timedCardAcceptanceType:
          timedCardAcceptanceSettings?.timedCardAcceptanceType,
        timedCardAcceptanceNumberOfDays:
          timedCardAcceptanceSettings?.timedCardAcceptanceNumberOfDays,
        originApplication:
          timedCardAcceptanceApplicationSettings?.originApplication,
        cardEnabled: timedCardAcceptanceApplicationSettings?.cardEnabled,
        enabled: timedCardAcceptanceApplicationSettings?.enabled,
      },
      customTemplatesSettings: customTemplatesSettings,
    },
  };

  return paymentRules;
};

export const mapOriginatingApplication = (
  originatingApplication: string,
): OriginSystem => {
  switch (originatingApplication.toLowerCase()) {
    case 'paymentlinks':
    case 'hostedpaymentsnonvalidating':
    case 'hostedpaymentsvalidating':
    case 'collections':
    case 'clientconnect':
    case 'eic':
    case 'buyerportal':
    case 'invoicing': {
      return OriginSystem.bandp;
    }
    case 'bpn': {
      return OriginSystem.bpn;
    }
    default: {
      return null;
    }
  }
};
